import React from 'react';
import { Target, Lightbulb, Users, Rocket } from 'lucide-react';
import { IMAGES, CONTACT } from '../utils/constants';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import '../styles/HomePage.css';

const VisionPage = () => {
  const handleNavigation = (section) => {
    if (section === 'vision') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      document.getElementById(section)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="vision-page pt-20">
      <SEOHead 
        title="Founder's Vision - Nitin Singh | SolidCloud International"
        description="Learn about our vision for the future of aerogel materials from founder Nitin Singh. Building sustainable, efficient, and transformative industrial solutions through India-led innovation."
        keywords="Nitin Singh, materials innovation, aerogel vision, sustainable engineering, India deep tech, collaborative innovation"
      />
      {/* Hero Section for Vision */}
      <section className="vision-hero relative py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="grid grid-cols-12 grid-rows-8 h-full w-full">
            {[...Array(96)].map((_, i) => (
              <div key={i} className="border border-white/10" />
            ))}
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="text-center mb-16">
            <h1 className="text-5xl sm:text-6xl font-bold mb-6">Founder's Vision</h1>
            <div className="w-24 h-1 bg-cream-400 mx-auto mb-8" />
            <p className="text-xl text-cream-100 max-w-3xl mx-auto">
              Building the future of advanced materials through innovation, collaboration, and sustainable engineering
            </p>
          </div>
        </div>
      </section>

      {/* Founder Introduction */}
      <section className="founder-intro section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="founder-image-wrapper">
              <div className="w-full flex justify-center">
                <div className="relative w-80 h-80">
                  <img 
                    src={IMAGES.founderPhoto} 
                    alt={CONTACT.founderName}
                    className="w-full h-full object-cover rounded-full shadow-2xl"
                    style={{
                      objectPosition: 'center',
                      aspectRatio: '1/1'
                    }}
                  />
                </div>
              </div>
              <div className="text-center mt-6">
                <h3 className="text-2xl font-bold text-slate-900">{CONTACT.founderName}</h3>
                <p className="text-blue-600 font-semibold mt-2">Founder & Visionary</p>
              </div>
            </div>
            
            <div className="founder-content">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">A Message from Our Founder</h2>
              <div className="space-y-4 text-lg text-slate-700 leading-relaxed">
                <p>
                  At SolidCloud International, we believe that the future of industry lies in materials that are not just lighter and stronger, but fundamentally transformative in how they enable innovation.
                </p>
                <p>
                  Our journey began with a simple observation: traditional insulation materials were holding back progress in critical sectors—from electric vehicles to aerospace engineering. The challenge wasn't just about improving existing solutions; it was about reimagining what's possible.
                </p>
                <p>
                  Aerogel technology represents more than just a material science breakthrough. It's a platform for enabling the next generation of sustainable, efficient, and safe industrial systems. Every application we develop is driven by the potential to create meaningful impact—whether that's preventing thermal runaway in battery systems, reducing energy waste in manufacturing, or enabling space exploration.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Pillars */}
      <section className="vision-pillars section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="section-header text-center mb-16">
            <h2 className="section-title">Our Core Vision</h2>
            <div className="title-underline" />
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="vision-pillar-card">
              <div className="pillar-icon-wrapper">
                <Target className="w-12 h-12" />
              </div>
              <h3 className="pillar-title">Innovation Through Science</h3>
              <p className="pillar-description">
                Pushing the boundaries of material science to create aerogel solutions that don't just meet industry standards—they redefine them. Our R&D is focused on breakthrough applications that solve real-world challenges.
              </p>
            </div>

            <div className="vision-pillar-card">
              <div className="pillar-icon-wrapper">
                <Lightbulb className="w-12 h-12" />
              </div>
              <h3 className="pillar-title">Sustainable Engineering</h3>
              <p className="pillar-description">
                Every product we develop is designed with sustainability at its core. From reducing energy consumption in industrial processes to enabling cleaner transportation, our materials contribute to a more efficient future.
              </p>
            </div>

            <div className="vision-pillar-card">
              <div className="pillar-icon-wrapper">
                <Users className="w-12 h-12" />
              </div>
              <h3 className="pillar-title">Collaborative Progress</h3>
              <p className="pillar-description">
                The future of materials innovation is collaborative. We partner with research institutions, OEMs, and industry pioneers to co-develop solutions that are market-ready, scalable, and transformative.
              </p>
            </div>

            <div className="vision-pillar-card">
              <div className="pillar-icon-wrapper">
                <Rocket className="w-12 h-12" />
              </div>
              <h3 className="pillar-title">India-Led Innovation</h3>
              <p className="pillar-description">
                Building from India for the world. We're proud to be at the forefront of deep-tech material science innovation, demonstrating that breakthrough technologies can emerge from anywhere when vision meets execution.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Future Roadmap */}
      <section className="future-roadmap section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="section-header text-center mb-16">
            <h2 className="section-title">Our Roadmap</h2>
            <div className="title-underline" />
            <p className="section-subtitle mt-6">
              Strategic milestones toward becoming the global leader in aerogel innovation
            </p>
          </div>

          <div className="roadmap-timeline">
            <div className="timeline-item">
              <div className="timeline-marker">
                <div className="timeline-dot" />
              </div>
              <div className="timeline-content">
                <h3 className="timeline-title">Near Term (2026-2027)</h3>
                <ul className="timeline-list">
                  <li>Launch commercial-grade aerogel products for EV battery thermal management</li>
                  <li>Establish partnerships with 3 major automotive OEMs</li>
                  <li>Scale manufacturing capabilities to support pilot deployments</li>
                  <li>Expand R&D team to accelerate aerospace applications</li>
                </ul>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">
                <div className="timeline-dot" />
              </div>
              <div className="timeline-content">
                <h3 className="timeline-title">Mid Term (2027-2029)</h3>
                <ul className="timeline-list">
                  <li>Achieve cost parity with traditional insulation materials at scale</li>
                  <li>Launch industrial thermal management product line</li>
                  <li>Establish global distribution network across key markets</li>
                  <li>Develop next-generation aerogel composites with enhanced properties</li>
                </ul>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">
                <div className="timeline-dot" />
              </div>
              <div className="timeline-content">
                <h3 className="timeline-title">Long Term (2030+)</h3>
                <ul className="timeline-list">
                  <li>Become the preferred aerogel supplier for aerospace and defense sectors</li>
                  <li>Achieve carbon-neutral manufacturing operations</li>
                  <li>Expand into emerging applications: quantum computing cooling, medical devices</li>
                  <li>Establish SolidCloud Research Institute for open innovation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="vision-cta section-padding bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Join Us in Shaping the Future</h2>
          <p className="text-xl text-blue-100 mb-8">
            Whether you're a researcher, an industry partner, or an innovator, we invite you to be part of this journey. Together, we can build materials that don't just perform better—they enable possibilities that didn't exist before.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`mailto:${CONTACT.founderEmail}`}
              className="inline-block bg-white/10 backdrop-blur-sm text-white font-semibold px-8 py-4 rounded-lg border-2 border-white/30 hover:bg-white/20 transition"
            >
              Connect with {CONTACT.founderName}
            </a>
            <a
              href={`mailto:${CONTACT.generalEmail}`}
              className="inline-block bg-white/10 backdrop-blur-sm text-white font-semibold px-8 py-4 rounded-lg border-2 border-white/30 hover:bg-white/20 transition"
            >
              Partnership Inquiries
            </a>
          </div>
        </div>
      </section>

      <Footer onNavigate={handleNavigation} />
    </div>
  );
};

export default VisionPage;
